/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import MediaPrepHeaderSection from '../../../common/commonComponents/mediaPrepheaderSection';
import MediaPrepPageHeader from '../../../common/commonComponents/mediaPrepPageHeader';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import MediaPreparationTable from "../../../common/commonComponents/mediaPreparationTable";
import { gptTestService } from "../../../../service/gptTestServices";
import { DEFAULT_PAGE_SIZE } from "../../../../constants/constants";
import { getColumns } from "./partials/column";
import { userService as plateRelatedService } from '../../../../service/plateServices';
import GPTTestFilterBar from "./partials/GPTTestFilterBar";
import Pagination from "../../../../widgets/pagination";
import GPTTestDetails from './views/gptTestDetails';
import CustomToast from '../../../../widgets/toaster';
import { userService } from '../../../../service/userService';


const GPTTest: React.FC = () => {
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const { t } = useTranslation();
  const [inventoryItems, setInventoryItems] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(0);

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [lotFilter, setLotFilter] = useState('');
  const [mediaTypeFilter, setMediaTypeFilter] = useState('');
  const [plateTypes, setPlateTypes] = useState<any[]>([]);
  const [view, setView] = useState<"list" | "details">("list");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("");
  const [loading, setLoading] = useState(false);
  
  const [sort, setSort] = useState("id");
      const [order, setOrder] = useState("desc");
      const handleSort = (key: string, value: boolean) => {
        setSort(key);
        setOrder(value ? "asc" : "desc");
        setCurrentPage(1);
      };

  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);

    if (size) {
      setPageSize(size);
    }
  };

  const [permission, setPermission] = useState<any>([]);
	const getPermissionByRole = async (role: number) => {
		try {
		const response = await userService.getPermissionByRole(role);

		if (response.status && response.statusCode === 200) {
			setPermission(response.data.permissions);
		}
		} catch (error) {
		console.error(error);
		}
	};

	useEffect(() => {
		const storedData = localStorage.getItem("userDetails");

		if (storedData) {
		const storedObject = JSON.parse(storedData);
		getPermissionByRole(storedObject[0].role_master_id);
		}
	}, []);

  const showToastMessage = (message: string, type: string) => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };


  const getMediaPowderListGpt = async () => {
    setLoading(true);
    try {
      const response = await gptTestService.getMediaPowderListGpt(
        currentPage,
        pageSize,
        sort,
        order,
        searchTerm,
        lotFilter,
        mediaTypeFilter,
      );

      if (response.status && response.statusCode === 200) {
        const mappedData = response.data.map((element: any) => ({
          id: element.id,
          batchCode: element.batch_name,
          name: element.media_name,
          mf: element.manufacturer_name,
          storage: element.storage_temperature,
          stock: element.available_stock,
          expiry: element.expiry_date,
          qaStatus: element.qa_release_status,
          stockStatus: element.stock_status,
          quantity: element.quantity,
          sku: element.catalog_number,
        }));

        setInventoryItems(mappedData);
        setTotalPages(response.totalPages);
      } else {
        setInventoryItems([]);
      }
    } catch (error) {
      console.error("API error:", error);
    }
    finally {
      setLoading(false);
    }
  };

  const isBatchExpired = (expiry: string) => {
    const [day, month, year] = expiry.split("-").map(Number);

    const expiryDate = new Date(year, month - 1, day);
    expiryDate.setHours(23, 59, 59, 999);

    return expiryDate < new Date();
  };


  const getPlateTypeList = async () => {
    try {
      const response = await plateRelatedService.listAllPlateTypes();

      if (response.status && response.statusCode === 200) {
        const formatted = response.data.map((item: any) => ({
          id: item.id,
          name: item.plate_type,
        }));

        setPlateTypes(formatted);
      }
    } catch (error) {
      console.error('Plate type API error:', error);
    }
  };

  useEffect(() => {
    getMediaPowderListGpt();
  }, [currentPage, pageSize, sort, order, searchTerm, lotFilter, mediaTypeFilter]);


  useEffect(() => {
    getPlateTypeList();
  }, []);


  
  return (
    <div className="media-prep-scope">
    <MediaPrepHeaderSection />
    <MediaPrepPageHeader pageHeader={t('gptTest.pageHeader')} pageDescription={t('pageDescription.gptTest.description')} />
    <CustomToast
      show={showToast}
      onClose={() => setShowToast(false)}
      delay={2500}
      message={toastMessage}
      toastType={toastType}
    />

    {view === "details" && selectedBatchId ? (
      <GPTTestDetails
        batchId={selectedBatchId}
        onFinish={() => {
          setView("list");
          setSelectedBatchId(null);
          getMediaPowderListGpt();
        }}
        showToastMessage={showToastMessage}
      />
    ) : (
      <>
      {/* Sticky Header */}
      <div className="sticky top-[110px] z-40 bg-white border-b border-slate-200 px-8 py-2 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#2D3E50] flex items-center justify-center text-white shadow">
            <span className="material-symbols-outlined text-xl">{t('gptTest.icons.science')}</span>
          </div>
          <div>
            <h2 className="text-lg font-black text-[#2D3E50] tracking-tight uppercase leading-tight">{t('gptTest.title')}</h2>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest leading-tight">{t('gptTest.description')}</p>
          </div>
        </div>

        {/* <div className="flex items-center gap-5">
          <div className="text-right">
            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Protocol Compliance</p>
            <p className="text-[10px] font-bold text-emerald-600">ISO/IEC 17025 Compliant</p>
          </div>
          <div className="h-8 w-px bg-slate-100"></div>
          <p className="text-[11px] font-black text-[#2D3E50] font-mono bg-slate-50 px-2 py-1 rounded border border-slate-100">MB-GPT-2026-REF</p>
        </div> */}
        
      </div>

      <div className="p-10 space-y-4 max-w-7xl mx-auto w-full pb-2 overflow-y-auto">
        {/* 1. Powder Batch Selection Section */}
        <section className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden animate-in fade-in duration-500">
          <div className="px-8 py-2 border-b border-slate-100 bg-slate-50/20 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-[#2D3E50] text-white flex items-center justify-center text-xs font-black shadow-sm">1</div>
              <h3 className="text-xs font-black text-[#2D3E50] uppercase tracking-[0.15em]">{t('gptTest.tableColumns.title')}</h3>
            </div>
          </div>
          <div className="px-5 pt-3 pb-5 space-y-3">
            <GPTTestFilterBar
              mediaTypes={plateTypes}
              selectedMediaType={mediaTypeFilter}
              searchTerm={searchInput}
              onMediaTypeChange={setMediaTypeFilter}
              onSearchChange={(val) => {
                setSearchInput(val);

                if (!val) {
                  setSearchTerm("");
                  setCurrentPage(1);
                }
              }}
              onSearchClick={() => {
                setSearchTerm(searchInput);
                setCurrentPage(1);
              }}
              onClearFilters={() => {
                setMediaTypeFilter("");
                setSearchInput("");
                setSearchTerm("");
              }}
            />

            <MediaPreparationTable
                columns={getColumns(
                  t,
                  selectedBatchId,
                  setSelectedBatchId,
                  permission?.includes("start_gpt")
                )}
                data={inventoryItems}
                loading={loading}
                currentPage={currentPage}
                pageSize={pageSize}
                onSortClick={handleSort}
                emptyMessage="No GPT batches available"
              />

            <div className="mt-2 flex justify-end">
              {permission?.includes("start_gpt") && (
              <button 
                onClick={() => {if (!selectedBatchId) return;

                  const selectedBatch = inventoryItems.find((item) => item.id === selectedBatchId);
                  if (selectedBatch && isBatchExpired(selectedBatch.expiry)) {
                    showToastMessage(
                      t('gptTest.gptTestDetails.expiryError'),
                      "error"
                    );
                    return;
                  }

                  setView("details");
                }}
                disabled={!selectedBatchId}
                className="bg-[#2D3E50] text-white px-6 py-2 rounded-xl shadow-xl shadow-slate-200 font-black text-xs uppercase tracking-[0.2em] hover:brightness-110 active:scale-95 transition-all disabled:opacity-30 disabled:grayscale disabled:cursor-not-allowed flex items-center gap-3"
              >
                {t('gptTest.tableColumns.startGpt')}
                <span className="material-symbols-outlined text-sm">{t('gptTest.icons.arrow_forward')}</span>
              </button>
              )}
            </div>
          </div>
        </section>

        <Pagination
          page={currentPage}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
        />
        
      </div>
      </>
    )}
    </div>
  );
};

export default GPTTest;
